import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import * as api from '../services/api';

const PREFERENCES_KEY = 'tfm_admin_preferences';

interface AdminPreferences {
  compactTables: boolean;
  confirmLifecycleActions: boolean;
  showUserEmailInTables: boolean;
  rememberLastFilters: boolean;
  activityLogPageSize: number;
  usersPageSize: number;
}

const defaultPreferences: AdminPreferences = {
  compactTables: false,
  confirmLifecycleActions: true,
  showUserEmailInTables: true,
  rememberLastFilters: true,
  activityLogPageSize: 25,
  usersPageSize: 25
};

function loadPreferences(): AdminPreferences {
  try {
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return defaultPreferences;
    return { ...defaultPreferences, ...(JSON.parse(raw) as Partial<AdminPreferences>) };
  } catch {
    return defaultPreferences;
  }
}

export function SettingsPage() {
  const location = useLocation();

  const [preferences, setPreferences] = useState<AdminPreferences>(defaultPreferences);
  const [prefsSaved, setPrefsSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const passwordsMatch = confirmPassword.length === 0 || newPassword === confirmPassword;
  const meetsLength = newPassword.length === 0 || newPassword.length >= 6;
  const differsFromCurrent = newPassword.length === 0 || newPassword !== currentPassword;

  useEffect(() => {
    setPreferences(loadPreferences());
  }, []);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  function updatePreference<K extends keyof AdminPreferences>(key: K, value: AdminPreferences[K]) {
    setPrefsSaved(false);
    setPreferences((prev) => ({ ...prev, [key]: value }));
  }

  function handlePreferencesSubmit(event: FormEvent) {
    event.preventDefault();
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
    setPrefsSaved(true);
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('New password must be different from the current password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setPasswordSubmitting(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordSubmitting(false);
    }
  }

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <p className="brand-mark">Account</p>
        <h2>Settings</h2>
        <p>Manage your password and local display preferences for the admin portal.</p>
      </header>

      <div className="settings-sections">
        <section id="change-password" className="settings-section">
          <header className="settings-section-header">
            <h3>Change password</h3>
            <p>
              Update the password for your Super Admin account. You will stay signed in after
              saving.
            </p>
          </header>

          <form className="settings-section-body" onSubmit={handlePasswordSubmit}>
            <div className="settings-fields">
              <label>
                Current password
                <div className="password-field">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowCurrent((value) => !value)}
                    aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrent ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>

              <label>
                New password
                <div className="password-field">
                  <input
                    type={showNew ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNew((value) => !value)}
                    aria-label={showNew ? 'Hide new password' : 'Show new password'}
                  >
                    {showNew ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>

              <label>
                Confirm new password
                <div className="password-field">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirm((value) => !value)}
                    aria-label={
                      showConfirm ? 'Hide confirmation password' : 'Show confirmation password'
                    }
                  >
                    {showConfirm ? 'Hide' : 'Show'}
                  </button>
                </div>
              </label>
            </div>

            <ul className="password-hints" aria-live="polite">
              <li className={meetsLength ? 'is-met' : undefined}>At least 6 characters</li>
              <li className={differsFromCurrent ? 'is-met' : undefined}>
                Different from current password
              </li>
              <li className={passwordsMatch ? 'is-met' : undefined}>Confirmation matches</li>
            </ul>

            {passwordError ? (
              <p className="form-error" role="alert">
                {passwordError}
              </p>
            ) : null}
            {passwordSuccess ? (
              <p className="form-success" role="status">
                {passwordSuccess}
              </p>
            ) : null}

            <button
              type="submit"
              className="primary-btn account-submit settings-submit"
              disabled={passwordSubmitting}
            >
              {passwordSubmitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </section>

        <section id="preferences" className="settings-section">
          <header className="settings-section-header">
            <h3>Preferences</h3>
            <p>
              Local display preferences for this browser. Server-side preference sync is not
              available yet.
            </p>
          </header>

          <form className="settings-section-body" onSubmit={handlePreferencesSubmit}>
            <div className="settings-fields">
              <fieldset className="settings-fieldset">
                <legend>Tables</legend>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={preferences.compactTables}
                    onChange={(e) => updatePreference('compactTables', e.target.checked)}
                  />
                  <span>Use compact table density</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={preferences.showUserEmailInTables}
                    onChange={(e) => updatePreference('showUserEmailInTables', e.target.checked)}
                  />
                  <span>Show email addresses in user tables</span>
                </label>
              </fieldset>

              <fieldset className="settings-fieldset">
                <legend>Workflow</legend>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={preferences.confirmLifecycleActions}
                    onChange={(e) => updatePreference('confirmLifecycleActions', e.target.checked)}
                  />
                  <span>Confirm before activate / deactivate / suspend / reject</span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={preferences.rememberLastFilters}
                    onChange={(e) => updatePreference('rememberLastFilters', e.target.checked)}
                  />
                  <span>Remember last used list filters on this device</span>
                </label>
              </fieldset>

              <div className="settings-field-grid">
                <label>
                  Users page size
                  <select
                    value={preferences.usersPageSize}
                    onChange={(e) => updatePreference('usersPageSize', Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>

                <label>
                  Activity log page size
                  <select
                    value={preferences.activityLogPageSize}
                    onChange={(e) =>
                      updatePreference('activityLogPageSize', Number(e.target.value))
                    }
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
              </div>
            </div>

            {prefsSaved ? (
              <p className="form-success" role="status">
                Preferences saved on this device.
              </p>
            ) : null}

            <button type="submit" className="primary-btn account-submit settings-submit">
              Save preferences
            </button>
          </form>
        </section>
      </div>
    </section>
  );
}
